import { useCallback, useEffect, useState } from 'react';
import { Edit, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { getErrorMessage } from '../api/response';
import { createService, deleteService, listServices, updateService } from '../api/services';
import { CLINIC_CONFIG_TODO, getBranches, getTenant, getTenantSettings } from '../api/tenant';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import TodoPanel from '../components/TodoPanel';
import { LoadingState } from '../components/PageState';
import { activeStatus, getStatusConfig } from '../utils/status';
import { currency } from '../utils/format';

const emptyService = {
  name: '',
  description: '',
  price: '',
  duration_minutes: 30,
  is_active: true
};

const Configuracion = () => {
  const [tenant, setTenant] = useState(null);
  const [settings, setSettings] = useState(null);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyService);
  const [confirm, setConfirm] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const [tenantData, settingsData, branchData, serviceData] = await Promise.all([
        getTenant(),
        getTenantSettings(),
        getBranches(),
        listServices({ limit: 100 })
      ]);
      setTenant(tenantData);
      setSettings(settingsData);
      setBranches(branchData);
      setServices(serviceData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar la configuracion'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const openService = (service = null) => {
    setEditing(service);
    setForm(service ? {
      name: service.name || '',
      description: service.description || '',
      price: service.price || '',
      duration_minutes: service.duration_minutes || 30,
      is_active: Boolean(Number(service.is_active))
    } : emptyService);
    setModalOpen(true);
  };

  const saveService = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const payload = {
        ...form,
        price: Number(form.price),
        duration_minutes: Number(form.duration_minutes),
        is_active: form.is_active
      };
      if (editing) {
        await updateService(editing.id, payload);
        toast.success('Servicio actualizado');
      } else {
        await createService(payload);
        toast.success('Servicio creado');
      }
      setModalOpen(false);
      loadConfig();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar el servicio'));
    } finally {
      setSaving(false);
    }
  };

  const removeService = async () => {
    if (!confirm) return;

    try {
      setSaving(true);
      await deleteService(confirm.id);
      toast.success('Servicio eliminado');
      setConfirm(null);
      loadConfig();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo eliminar el servicio'));
    } finally {
      setSaving(false);
    }
  };

  const branchColumns = [
    { key: 'name', label: 'Sucursal' },
    { key: 'address', label: 'Direccion', render: (row) => row.address || '-' },
    { key: 'phone', label: 'Telefono', render: (row) => row.phone || '-' },
    { key: 'is_primary', label: 'Tipo', render: (row) => Number(row.is_primary) === 1 ? 'Principal' : 'Sucursal' }
  ];

  const serviceColumns = [
    { key: 'name', label: 'Servicio' },
    { key: 'price', label: 'Precio', render: (row) => currency(row.price) },
    { key: 'duration_minutes', label: 'Duracion', render: (row) => `${row.duration_minutes || 30} min` },
    {
      key: 'is_active',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(activeStatus, Number(row.is_active));
        return <StatusBadge label={status.label} color={status.color} />;
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openService(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => setConfirm(row)} className="rounded-md p-2 text-red-700 hover:bg-red-50" title="Eliminar">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Configuracion"
        description="Datos de clinica, sucursales, branding, politicas y servicios ofrecidos."
        actions={<Button icon={Plus} onClick={() => openService()}>Nuevo servicio</Button>}
      />

      <TodoPanel title="Edicion de configuracion pendiente" items={CLINIC_CONFIG_TODO} />

      {loading ? (
        <LoadingState rows={8} />
      ) : (
        <>
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <p className="text-sm font-semibold text-gray-500">Clinica</p>
              <h2 className="mt-2 text-xl font-bold text-gray-950">{tenant?.name || settings?.business?.business_name || 'Sin nombre'}</h2>
              <p className="mt-2 text-sm text-gray-600">{tenant?.email || settings?.business?.email || '-'}</p>
              <p className="text-sm text-gray-600">{tenant?.phone || settings?.business?.phone || '-'}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <p className="text-sm font-semibold text-gray-500">Branding app</p>
              <div className="mt-3 flex items-center gap-3">
                <span className="h-8 w-8 rounded-md border" style={{ backgroundColor: settings?.colors?.primary || '#2563eb' }} />
                <span className="h-8 w-8 rounded-md border" style={{ backgroundColor: settings?.colors?.secondary || '#0f172a' }} />
              </div>
              <p className="mt-3 text-sm text-gray-600">{settings?.logo_url ? 'Logo configurado' : 'Logo no configurado'}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <p className="text-sm font-semibold text-gray-500">Politicas</p>
              <p className="mt-2 text-sm text-gray-600 line-clamp-3">{settings?.policies?.appointments || tenant?.policies?.appointments || 'Sin politica de agenda'}</p>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold text-gray-950">Sucursales</h2>
            <Table columns={branchColumns} data={branches} emptyMessage="No hay sucursales configuradas" />
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold text-gray-950">Servicios ofrecidos</h2>
            <Table columns={serviceColumns} data={services} emptyMessage="No hay servicios configurados" />
          </section>
        </>
      )}

      <Modal
        open={modalOpen}
        title={editing ? 'Editar servicio' : 'Nuevo servicio'}
        onClose={() => setModalOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
            <Button type="submit" form="service-form" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        }
      >
        <form id="service-form" onSubmit={saveService} className="grid gap-4 md:grid-cols-2">
          <FormField label="Nombre" required>
            <input className={inputClassName} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
          </FormField>
          <FormField label="Precio" required>
            <input type="number" className={inputClassName} value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} required />
          </FormField>
          <FormField label="Duracion base">
            <input type="number" className={inputClassName} value={form.duration_minutes} onChange={(event) => setForm({ ...form, duration_minutes: event.target.value })} />
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={String(form.is_active)} onChange={(event) => setForm({ ...form, is_active: event.target.value === 'true' })}>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Descripcion">
              <textarea rows={3} className={inputClassName} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </FormField>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Eliminar servicio"
        message={`Se eliminara el servicio ${confirm?.name}.`}
        confirmLabel="Eliminar"
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={removeService}
      />
    </div>
  );
};

export default Configuracion;
